<template>
    <div>
        <p
            v-if="message"
            v-bind:class="messageClassObj">
            {{ message }}
        </p>
        <pc-radio-group
            v-if="cancelExplanationOptionObjects.length > 0"
            v-model="cancelExplanationType"
            name="cancelExplanationType"
            v-bind:label="cancelExplanationTypeLabel"
            required
            v-bind:validator="cancelExplanationTypeValidator"
            v-bind:options="cancelExplanationOptionObjects"
            class="mb-3" />
        <!--
            Using 2 different inputs depending on whether comment is required;
            hot-swapping validators for a single input doesn't seem to work as
            expected in all cases. Specifying "key" prop because otherwise Vue
            seems to recycle the component and it doesn't get properly reinitialized
            when replaced.
        -->
        <pc-input
            v-if="isOtherCancelExplanation"
            id="CancelExplanationDetailRequiredInput"
            key="CancelExplanationDetailRequiredInput"
            v-model="cancelExplanationDetail"
            required
            v-bind:label="cancelExplanationDetailLabelRequired"
            v-bind:validator="cancelExplanationDetailRequiredValidator"
            class="mb-3" />
        <pc-input
            v-else
            id="CancelExplanationDetailOptionalInput"
            key="CancelExplanationDetailOptionalInput"
            v-model="cancelExplanationDetail"
            v-bind:label="cancelExplanationDetailLabelAdditionalComments"
            class="mb-3" />
        <pc-alert v-bind="cancelDeterrentBinder" />
        <hr
            v-if="separateButtonRow"
            class="mx-n3" />
        <div class="d-flex align-items-center justify-content-end">
            <pc-button
                v-if="showCancelIncomplete"
                v-on:click="cancel(true)">
                {{ cancelIncompleteDocsButtonLabel }}
            </pc-button>
            <pc-button
                class="ms-2"
                v-on:click="cancel(false)">
                {{ cancelAllDocsButtonLabel }}
            </pc-button>
            <pc-button
                v-if="showDontCancel"
                primary
                class="ms-2"
                v-on:click="dontCancel">
                {{ dontCancelButtonLabel }}
            </pc-button>
        </div>
    </div>
</template>
<script>
    import { getValidator, VALIDATOR_TYPES } from "Validation/validator";
    import { ALERT_SEVERITY } from "Util/alerts";
    import { KEYS, NAMESPACE } from "./i18n/cancelSignaturesDialog.i18n";
    import { mapI18n } from "Util/i18n";
    import {
        ESIGNATURE_CANCEL_EXPLANATION_TYPE,
        getESignatureCancelExplanationLabel
    } from "ESignatures/eSignatureConstants";
    import { ValidationHub } from "Validation/validationHub";

    const cancelDeterrentKeysByExplanation = {
        [ESIGNATURE_CANCEL_EXPLANATION_TYPE.ResendingNotification]: KEYS.cancelDeterrentResending,
        [ESIGNATURE_CANCEL_EXPLANATION_TYPE.CorrectingRecipientContactInfo]: KEYS.cancelDeterrentCorrectingRecipient
    };

    export default {
        provide() {
            return {
                validationHub: this.validationHub
            };
        },
        props: {
            message: { type: String, required: false, default: null },
            showCancelIncomplete: { type: Boolean, required: false, default: false },
            separateButtonRow: { type: Boolean, required: false, default: false },
            showDontCancel: { type: Boolean, required: false, default: false },
            cancelExplanationOptions: { type: Array, required: false, default: () => [] },
            showCancelDeterrents: { type: Boolean, required: false, default: false },
            isCPAdminCancel: { type: Boolean, required: false, default: false },
            messageClassObj: { type: Object, required: false, default: null }
        },
        emits: ["cancel-incomplete", "cancel-all", "dont-cancel"],
        data() {
            return {
                cancelExplanationDetail: null,
                cancelExplanationType: this.isCPAdminCancel
                    ? ESIGNATURE_CANCEL_EXPLANATION_TYPE.CpAdminCancellation
                    : null,
                validationHub: new ValidationHub()
            };
        },
        computed: {
            isOtherCancelExplanation() {
                return this.cancelExplanationType === ESIGNATURE_CANCEL_EXPLANATION_TYPE.Other;
            },
            cancelExplanationTypeValidator() {
                return getValidator(this.$app, {
                    type: VALIDATOR_TYPES.required,
                    overrideMsg: this.cancelExplanationDetailOverrideMsg
                });
            },
            cancelExplanationDetailRequiredValidator() {
                return getValidator(this.$app, {
                    type: VALIDATOR_TYPES.required
                });
            },
            cancelExplanationOptionObjects() {
                if (!this.cancelExplanationOptions) {
                    return [];
                }
                return this.cancelExplanationOptions.map((opt) => {
                    return {
                        text: getESignatureCancelExplanationLabel(this.$app, opt),
                        value: opt
                    };
                });
            },
            cancelDeterrentMessage() {
                if (!this.showCancelDeterrents || this.cancelExplanationType == null) {
                    return null;
                }
                const cancelDeterrentKey = cancelDeterrentKeysByExplanation[this.cancelExplanationType];

                if (!cancelDeterrentKey) {
                    return null;
                }
                // Choose appropriate i18n computed based on key that maps to reason.
                return this[cancelDeterrentKey];
            },
            cancelDeterrentBinder() {
                return {
                    shown: Boolean(this.cancelDeterrentMessage),
                    severity: ALERT_SEVERITY.info,
                    message: this.cancelDeterrentMessage
                };
            },
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            )
        },
        methods: {
            cancel(incompleteOnly) {
                const validationResults = this.validationHub.validate();

                if (validationResults && validationResults.length > 0) {
                    return;
                }

                // Be careful changing the shape of this object. There are upstream dependencies on
                // the names of these props.
                const cancelExplanationData = {
                    explanationType: this.cancelExplanationType,
                    explanationDetail: this.cancelExplanationDetail
                };

                if (incompleteOnly) {
                    this.$emit("cancel-incomplete", cancelExplanationData);
                } else {
                    this.$emit("cancel-all", cancelExplanationData);
                }
            },
            dontCancel() {
                this.$emit("dont-cancel");
            }
        }
    };
</script>
